import { AbstractMessageCommand } from 'flux-connection';
import { Injectable, Inject, Optional } from '@angular/core';
import { DataStore } from 'flux-store';
import { CommandInterfaces, Command, StateService } from 'flux-core';
import { DiagramInfoModel, DiagramInfoModelStore } from 'flux-diagram';
import { Observable, of, from } from 'rxjs';
import { tap, map, switchMap, take } from 'rxjs/operators';

/**
 * This is the command which changes the role of the diagram collaborator and send
 * the request to the server.
 *
 * data: {
 *     collabs { id, role} An array of changed collaborator id and role
 *     diagramId id of the resource
 * }
 */
@Injectable()
@Command()
export class ChangeDiagramCollaborators extends AbstractMessageCommand  {

    public static get dataDefinition(): {}  {
        return {
            collabs: true,
            diagramId: true,
        };
    }

    public static get implements(): Array<CommandInterfaces> {
        return [
            'IMessageCommand',
            'IDiagramCommand',
        ];
    }

    constructor(
        protected dataStore: DataStore,
        @Optional() @Inject( StateService ) protected state?: StateService<any, any>,
    ) {
        super()/* istanbul ignore next */;
    }

    public get version(): number {
        return 2;
    }

    public prepareData() {
        this.previousData = {};
        const modelStore = this.dataStore.getModelStore( DiagramInfoModel ) as DiagramInfoModelStore;
        return modelStore.getCollabs( this.data.diagramId ).pipe(
            tap( collaborators => {
                this.previousData.collabs = collaborators.filter( collab =>
                    this.data.collabs.map( user => user.id ).indexOf( collab.id ) !== -1 );
            }),
        );
    }

    /**
     * execute
     * change collaborator role
     */
    public execute(): Observable<any> {
        const modelStore = this.dataStore.getModelStore( DiagramInfoModel ) as DiagramInfoModelStore;

        return of( this.previousData.collabs ).pipe(
            map( collaborators => {
                if ( !Array.isArray( this.data?.collabs ) || !Array.isArray( collaborators )) {
                    return [];
                }
                return this.data.collabs
                    .map( collab  => {
                        const collabData = collaborators.find( collaborator => collaborator.id === collab.id ) || collab;
                        if ( !collabData ) {
                            return null;
                        }
                        return { ...collabData, role: collab.role };
                    })
                    .filter( collab => !!collab );
            }),
            switchMap( collabs => modelStore.storageMergeCollabs( this.data.diagramId, collabs ).pipe(
                switchMap(() => this.updateEffectivePermissionRole( modelStore, this.data.diagramId, collabs )),
            )),
        );
    }

    /**
     * executeResult
     * Update the collaborators with the response
     */
    public executeResult( response: any ): Observable<any> {
        const modelStore = this.dataStore.getModelStore( DiagramInfoModel ) as DiagramInfoModelStore;
        const responseCollabs = Array.isArray( response?.collabs ) ? response.collabs.filter( collab => !!collab ) : [];
        if ( responseCollabs.length > 0 ) {
            return modelStore.storageMergeCollabs( this.resourceId, responseCollabs ).pipe(
                switchMap(() => this.updateEffectivePermissionRole( modelStore, this.resourceId, responseCollabs )),
            );
        }

        if ( Array.isArray( this.data?.collabs ) && this.data.collabs.length > 0 ) {
            return modelStore.getCollabs( this.resourceId ).pipe(
                take( 1 ),
                map( existingCollabs => this.data.collabs
                    .map( collab => {
                        const existing = ( existingCollabs || [] ).find( c => c.id === collab.id );
                        if ( existing ) {
                            return { ...existing, role: collab.role };
                        }
                        return collab;
                    })
                    .filter( collab => !!collab )),
                switchMap( collabs => modelStore.storageMergeCollabs( this.resourceId, collabs ).pipe(
                    switchMap(() => this.updateEffectivePermissionRole( modelStore, this.resourceId, collabs )),
                )),
            );
        }

        return of( true );
    }


    public revert(): Observable<any> {
        const modelStore = this.dataStore.getModelStore( DiagramInfoModel ) as DiagramInfoModelStore ;
        return modelStore.storageMergeCollabs( this.data.diagramId, this.previousData.collabs );
    }

    protected updateEffectivePermissionRole(
        modelStore: DiagramInfoModelStore,
        resourceId: string,
        collabs: any[],
    ): Observable<any> {
        const currentUserId = this.state?.get( 'CurrentUser' );
        if ( !currentUserId || !resourceId || !Array.isArray( collabs )) {
            return of( true );
        }
        const currentUserCollab = collabs.find( collab => collab?.id === currentUserId && collab?.role !== undefined );
        if ( !currentUserCollab ) {
            return of( true );
        }
        return from( modelStore.updateStatic(
            { id: resourceId },
            { $set: { effectivePermissionRole: currentUserCollab.role }},
        ));
    }

}

Object.defineProperty( ChangeDiagramCollaborators, 'name', {
    value: 'ChangeDiagramCollaborators',
});
